import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTimer } from "react-timer-hook";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import OtpInput from "react-otp-input";
import ReCAPTCHA from "react-google-recaptcha-enterprise";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { useLogin } from "../../store/backend/useLogin";
import {
  useGenerateOtp,
  useLoginWithOtp,
  useChangePassword,
} from "store/backend/assets";
import useAuth from "../../hooks/useAuth";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import { ErrorMessage, Heading1, PrimaryButton, Textfield } from "widgets";
import Loader from "../../Shared/Loader/Loader";
import PasswordField from "widgets/PasswordField/PasswordField";
import { removeToken } from "utils/tokenUtils";
import { SITE_KEY } from "constants/config";

import styles from "./Login.module.scss";

const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

const handleGenerateOtp = async ({
  phone,
  recaptchaRef,
  generateOtp,
  openSnackbar,
  setFieldValue,
  restartTimer,
  seconds,
  otp,
}) => {
  if (otp && otp.length === 4) {
    setFieldValue("otp", "");
    return;
  }
  if (!isValidPhone(phone)) {
    openSnackbar("Please enter a valid 10-digit phone number", "error");
    return;
  }

  try {
    const captchaToken = await recaptchaRef.current.executeAsync(
      "generate_otp"
    );
    recaptchaRef.current.reset();
    if (!captchaToken) {
      openSnackbar(
        "Multiple OTP requests detected. Please try again after 2 minutes.",
        "error"
      );
      return;
    }

    generateOtp(
      { phone, captcha_token: captchaToken },
      {
        onSuccess: () => {
          openSnackbar("OTP sent successfully!", "success");
          setFieldValue("step", "FORGOT_OTP");
          const newExpiry = new Date();
          newExpiry.setSeconds(newExpiry.getSeconds() + 60);
          restartTimer(newExpiry);
        },
        onError: (error) => {
          openSnackbar(error?.message || "Error sending OTP", "error");
        },
      }
    );
  } catch (err) {
    openSnackbar(err?.message || "Failed to get captcha token", "error");
  }
};

const handleOnSuccessLogin = (data, authLogin, navigate, mobile) => {
  authLogin(data.access_token);
  localStorage.setItem("sales_person_id", data.crn_id);
  localStorage.setItem("roles", data.roles);
  localStorage.setItem("phoneNumber", mobile);
  localStorage.setItem("sales_person_uam_id", data.uam_id);
  navigate("/homePage");
};

const Login = () => {
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const recaptchaRef = useRef(null);

  const { mutate: generateOtp, isPending: isOtpLoading } = useGenerateOtp();
  const { mutate: loginWithOtp, isPending: isLoggingIn } = useLoginWithOtp();
  const { mutate: changePassword, isPending: isChangePasswordLoading } =
    useChangePassword();
  const {
    mutate: login,
    isPending: isLoginLoading,
    isError,
    error,
  } = useLogin();

  const initialExpiry = new Date();
  const { seconds, restart } = useTimer({
    expiryTimestamp: initialExpiry,
    autoStart: false,
  });

  return (
    <Formik
      initialValues={{
        step: "LOGIN",
        mobile: "",
        password: "",
        mobileForForgotPassword: "",
        otp: "",
        newPassword: "",
        confirmPassword: "",
      }}
      validationSchema={Yup.object({
        mobile: Yup.string().when("step", {
          is: "LOGIN",
          then: (schema) =>
            schema
              .required("Mobile number is required")
              .matches(
                /^[0-9]{10}$/,
                "Mobile number must be exactly 10 digits"
              ),
        }),
        password: Yup.string().when("step", {
          is: "LOGIN",
          then: (schema) =>
            schema
              .required("Password is required")
              .min(6, "At least 6 characters"),
        }),
        mobileForForgotPassword: Yup.string().matches(
          /^[0-9]{10}$/,
          "Mobile number must be exactly 10 digits"
        ),
        newPassword: Yup.string().when("step", {
          is: "CHANGE_PASSWORD",
          then: (schema) =>
            schema
              .required("New password is required")
              .min(6, "Minimum 6 characters")
              .max(64, "Maximum 64 characters")
              .test(
                "no-spaces",
                "Password must not contain spaces",
                (value) => value && !/\s/.test(value)
              )
              .test(
                "no-consecutive-dots",
                "Password must not contain consecutive dots (..)",
                (value) => value && !value.includes("..")
              )
              .test(
                "no-consecutive-underscores",
                "Password must not contain consecutive underscores (__)",
                (value) => value && !value.includes("__")
              )
              .trim(),
        }),
        confirmPassword: Yup.string().when("step", {
          is: "CHANGE_PASSWORD",
          then: (schema) =>
            schema
              .required("Please confirm your password")
              .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
        }),
      })}
      onSubmit={(values, { setFieldValue, resetForm }) => {
        if (values.step === "LOGIN") {
          login(values, {
            onSuccess: (data) =>
              handleOnSuccessLogin(data, authLogin, navigate, values.mobile),
            onError: (err) =>
              openSnackbar(err?.message || "Unable to log in", "error"),
          });
        } else if (values.step === "FORGOT_PHONE") {
          handleGenerateOtp({
            phone: values.mobileForForgotPassword,
            recaptchaRef,
            generateOtp,
            openSnackbar,
            setFieldValue,
            restartTimer: restart,
            seconds,
            otp: values.otp,
          });
        } else if (values.step === "FORGOT_OTP") {
          loginWithOtp(
            { phone: values.mobileForForgotPassword, otp: values.otp },
            {
              onSuccess: (data) => {
                authLogin(data.access_token);
                openSnackbar("OTP verified successfully!", "success");
                setFieldValue("step", "CHANGE_PASSWORD");
              },
              onError: (err) =>
                openSnackbar(err?.message || "Invalid OTP", "error"),
            }
          );
        } else if (values.step === "CHANGE_PASSWORD") {
          changePassword(
            {
              new_password: values.newPassword,
            },
            {
              onSuccess: () => {
                openSnackbar(
                  "Password changed successfully! Please log in.",
                  "success"
                );
                removeToken();
                resetForm();
                setFieldValue("step", "LOGIN");
              },
              onError: (err) =>
                openSnackbar(
                  err?.message || "Failed to change password",
                  "error"
                ),
            }
          );
        }
      }}
    >
      {({ values, setFieldValue, handleSubmit, errors }) => (
        <Form onSubmit={handleSubmit} autoComplete="off">
          <ReCAPTCHA sitekey={SITE_KEY} size="invisible" ref={recaptchaRef} />

          {values.step === "LOGIN" && (
            <div className={styles.loginContainer}>
              <Heading1 heading="Login" />
              <div>
                <Textfield
                  id="mobile"
                  label="Mobile"
                  type="text"
                  value={values.mobile}
                  handleOnChange={(e) =>
                    setFieldValue(
                      "mobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                />
                {errors.mobile && <ErrorMessage>{errors.mobile}</ErrorMessage>}
              </div>
              <PasswordField
                id="password"
                label="Password"
                value={values.password}
                onChange={(e) => setFieldValue("password", e.target.value)}
                error={errors.password}
              />
              {isError && (
                <ErrorMessage>
                  {error?.message || "Unable to log in"}
                </ErrorMessage>
              )}
              <div
                className={styles.changePasswordLink}
                onClick={() => setFieldValue("step", "FORGOT_PHONE")}
              >
                Change Password
              </div>
              <div className={styles.btnContainer}>
                <PrimaryButton type="submit" disabled={isLoginLoading}>
                  {isLoginLoading ? <Loader /> : "Login"}
                </PrimaryButton>
              </div>
            </div>
          )}

          {values.step === "FORGOT_PHONE" && (
            <div className={styles.loginContainer}>
              <ArrowBackIosIcon
                className={styles.goBackIcon}
                onClick={() => setFieldValue("step", "LOGIN")}
              />
              <Heading1 heading="Please enter your phone number" />
              <Textfield
                id="mobileForForgotPassword"
                label="Enter Mobile Number"
                type="text"
                value={values.mobileForForgotPassword}
                handleOnChange={(e) =>
                  setFieldValue(
                    "mobileForForgotPassword",
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  )
                }
              />
              {errors.mobileForForgotPassword && (
                <ErrorMessage>{errors.mobileForForgotPassword}</ErrorMessage>
              )}

              <PrimaryButton
                type="submit"
                disabled={
                  !isValidPhone(values.mobileForForgotPassword) || isOtpLoading
                }
              >
                {isOtpLoading ? <Loader /> : "Request OTP"}
              </PrimaryButton>
            </div>
          )}

          {values.step === "FORGOT_OTP" && (
            <div className={styles.loginContainer}>
              <ArrowBackIosIcon
                className={styles.goBackIcon}
                onClick={() => setFieldValue("step", "FORGOT_PHONE")}
              />
              <div className={styles.heading}>OTP Verification</div>
              <div className={styles.subHeading}>
                We have sent an OTP to <b>{values.mobileForForgotPassword}</b>
              </div>
              <div className={styles.fieldWrapper}>
                <OtpInput
                  value={values.otp}
                  onChange={(val) => setFieldValue("otp", val)}
                  numInputs={4}
                  isInputNum
                  renderSeparator={<span>-</span>}
                  renderInput={(props) => (
                    <input {...props} className={styles.otpInput} />
                  )}
                  containerClassName={styles.otpContainer}
                />
              </div>
              <div className={styles.resendWrapper}>
                {seconds > 0 ? (
                  <div className={styles.resendOtpText}>
                    Resend OTP in {seconds}s
                  </div>
                ) : (
                  <div
                    onClick={() =>
                      handleGenerateOtp({
                        phone: values.mobileForForgotPassword,
                        recaptchaRef,
                        generateOtp,
                        openSnackbar,
                        setFieldValue,
                        restartTimer: restart,
                        seconds,
                      })
                    }
                    className={styles.resendOtpLink}
                  >
                    Resend OTP
                  </div>
                )}
              </div>
              <PrimaryButton type="submit" disabled={values.otp.length < 4}>
                {isLoggingIn ? <Loader /> : "Verify OTP"}
              </PrimaryButton>
            </div>
          )}
          {values.step === "CHANGE_PASSWORD" && (
            <div className={styles.loginContainer}>
              <ArrowBackIosIcon
                className={styles.goBackIcon}
                onClick={() => setFieldValue("step", "FORGOT_OTP")}
              />
              <div className={styles.heading}>Change Password</div>

              <PasswordField
                id="newPassword"
                label="New Password"
                value={values.newPassword}
                onChange={(e) => setFieldValue("newPassword", e.target.value)}
                error={errors.newPassword}
              />

              <PasswordField
                id="confirmPassword"
                label="Confirm Password"
                value={values.confirmPassword}
                onChange={(e) =>
                  setFieldValue("confirmPassword", e.target.value)
                }
                error={errors.confirmPassword}
              />

              <ul className={styles.passwordRules}>
                <li
                  className={`${styles.passwordRule} ${
                    values.newPassword.length >= 6
                      ? styles.valid
                      : styles.invalid
                  }`}
                >
                  <span>{values.newPassword.length >= 6 ? "✔" : "✖"}</span>
                  At least 6 characters
                </li>

                <li
                  className={`${styles.passwordRule} ${
                    values.newPassword.length <= 64 &&
                    values.newPassword.length > 0
                      ? styles.valid
                      : styles.invalid
                  }`}
                >
                  <span>
                    {values.newPassword.length <= 64 &&
                    values.newPassword.length > 0
                      ? "✔"
                      : "✖"}
                  </span>
                  No more than 64 characters
                </li>

                <li
                  className={`${styles.passwordRule} ${
                    values.newPassword && /^\S+$/.test(values.newPassword)
                      ? styles.valid
                      : styles.invalid
                  }`}
                >
                  <span>{/^\S+$/.test(values.newPassword) ? "✔" : "✖"}</span>
                  No spaces
                </li>

                <li
                  className={`${styles.passwordRule} ${
                    values.newPassword && !values.newPassword.includes("..")
                      ? styles.valid
                      : styles.invalid
                  }`}
                >
                  <span>{!values.newPassword.includes("..") ? "✔" : "✖"}</span>
                  No consecutive dots (..)
                </li>

                <li
                  className={`${styles.passwordRule} ${
                    values.newPassword && !values.newPassword.includes("__")
                      ? styles.valid
                      : styles.invalid
                  }`}
                >
                  <span>{!values.newPassword.includes("__") ? "✔" : "✖"}</span>
                  No consecutive underscores (__)
                </li>
              </ul>

              <PrimaryButton
                type="submit"
                disabled={
                  !values.newPassword ||
                  !values.confirmPassword ||
                  errors.newPassword ||
                  errors.confirmPassword
                }
              >
                {isChangePasswordLoading ? <Loader /> : "Change Password"}
              </PrimaryButton>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default Login;
